/**
 * V1.5 Portfolio Atlas — Segment × Phase Grid.
 * Quelle: Implementation Brief §7.3 + Screen-Inventory §4.
 *
 * Y-Achse: Segment (KI/IT/ProLo/V&S/HR — Brief Q15: bleibt Segment).
 * X-Achse: Phasen (Neu/Setup/Kickoff/Umsetzung/Endspurt/Handover).
 * Eine Zelle = ein oder mehrere Projekte. Position kodiert führendes Segment +
 * aktuelle Phase. Issues sind über Border-Inset-Shadow / Akzent-Farbe markiert.
 */
import type { LifecycleStatus } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DueDateBadge } from '../../components/v2/due-date-badge.js';
import {
  Chip,
  ChipRow,
  DueRangeFilter,
  SearchInput,
  Toggle,
} from '../../components/v2/filters/filter-primitives.js';
import { api } from '../../lib/api.js';
import { avatarInitials } from '../../lib/avatar.js';
import { usePersona } from '../../lib/persona.js';
import { useSession } from '../../lib/session.js';
import { decidePersonaDefaults } from '../../lib/sticky-filters.js';
import { useStickyFilters } from '../../lib/use-sticky-filters.js';

type Project = {
  project_id: string;
  project_key: string;
  name: string;
  customer_name: string;
  segment: 'KI' | 'IT' | 'ProLo' | 'V&S' | 'HR';
  phase_id: string;
  lifecycle_status: LifecycleStatus;
  staffing_needed: boolean;
  is_blocked?: boolean;
  qs_with_bl_open?: boolean;
  responsibility?: 'PU' | 'Service' | 'MTB' | null;
  ms_classification?: 'msPeople' | 'msSolution' | 'msService' | null;
  project_lead_user_id?: string | null;
  due_date?: string | null;
};

const SEGMENTS: Project['segment'][] = ['KI', 'IT', 'ProLo', 'V&S', 'HR'];
const RESPONSIBILITIES = ['PU', 'Service', 'MTB'] as const;
const MS_CLASSES = ['msPeople', 'msSolution', 'msService'] as const;

export function PortfolioAtlasPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const persona = usePersona();
  const projectsQuery = useQuery({
    queryKey: ['v2-atlas-projects', 'all'],
    queryFn: () => api.listProjects({}) as Promise<{ items: Project[] }>,
  });
  const phasesQuery = useQuery({ queryKey: ['v2-atlas-phases'], queryFn: () => api.listPhases() });

  // Filter-State
  const [search, setSearch] = useState('');
  const [segmentFilter, setSegmentFilter] = useState<string | null>(null);
  const [bereichFilter, setBereichFilter] = useState<string | null>(null);
  const [msFilter, setMsFilter] = useState<string | null>(null);
  const [needsOnly, setNeedsOnly] = useState(false);
  const [missingResponsibility, setMissingResponsibility] = useState(false);
  // PL-Default: nur eigene Projekte (abschaltbar) — siehe Persona-Default-Effekt unten.
  const [myProjectsOnly, setMyProjectsOnly] = useState(false);
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [dueFrom, setDueFrom] = useState('');
  const [dueTo, setDueTo] = useState('');

  // Sticky Filters (#63): gesetzte Filter überleben Seitenwechsel, Reload und
  // Gerätewechsel. Die Freitextsuche bleibt bewusst draußen.
  const sticky = useStickyFilters('atlas', {
    values: {
      segment: segmentFilter,
      bereich: bereichFilter,
      ms: msFilter,
      needs_only: needsOnly,
      missing_responsibility: missingResponsibility,
      my_projects_only: myProjectsOnly,
      include_completed: includeCompleted,
      overdue_only: overdueOnly,
      due_from: dueFrom,
      due_to: dueTo,
    },
    apply: (f) => {
      if (f.segment !== undefined) setSegmentFilter(f.segment ?? null);
      if (f.bereich !== undefined) setBereichFilter(f.bereich ?? null);
      if (f.ms !== undefined) setMsFilter(f.ms ?? null);
      if (f.needs_only !== undefined) setNeedsOnly(f.needs_only);
      if (f.missing_responsibility !== undefined)
        setMissingResponsibility(f.missing_responsibility);
      if (f.my_projects_only !== undefined) setMyProjectsOnly(f.my_projects_only);
      if (f.include_completed !== undefined) setIncludeCompleted(f.include_completed);
      if (f.overdue_only !== undefined) setOverdueOnly(f.overdue_only);
      if (f.due_from !== undefined) setDueFrom(f.due_from);
      if (f.due_to !== undefined) setDueTo(f.due_to);
    },
  });

  // Phase 9 — Persona-Default-Filter
  // Wenn Persona wechselt, sinnvolle Defaults setzen. User kann jederzeit
  // wegklicken — kein zwanghaftes Reset bei jedem Render.
  const blSegments: string[] = (user?.function_roles ?? [])
    .filter((fr) => fr.role === 'bereichsleiter')
    .map((fr) => fr.scope_segment)
    .filter((s): s is NonNullable<typeof s> => s !== null);
  const mtbSegments: string[] = (user?.function_roles ?? [])
    .filter((fr) => fr.role === 'mtb')
    .map((fr) => fr.scope_segment)
    .filter((s): s is NonNullable<typeof s> => s !== null);
  const svcSegments: string[] = (user?.function_roles ?? [])
    .filter((fr) => fr.role === 'service_leitung')
    .map((fr) => fr.scope_segment)
    .filter((s): s is NonNullable<typeof s> => s !== null);
  // Merkt sich, für welche Persona die Defaults zuletzt gesetzt wurden. `null` =
  // noch gar nicht. Damit lässt sich der erste Durchlauf (Seiteneinstieg) vom
  // späteren Persona-WECHSEL unterscheiden.
  const personaDefaultsAppliedFor = useRef<string | null>(null);
  useEffect(() => {
    if (persona.isLoading) return;
    // Die komplette Abwägung „Persona-Defaults vs. gemerkte Filter" steckt in
    // decidePersonaDefaults (lib/sticky-filters.ts) — inklusive der Regel, dass ein
    // FEHLGESCHLAGENER Präferenz-Abruf die Defaults zwar setzt, den Erstbesuch aber
    // nicht verbraucht (`mark: false`). Sonst überschriebe ein später doch noch
    // erfolgreicher Refetch die gerade hydrierten gemerkten Filter.
    const decision = decidePersonaDefaults({
      hydrated: sticky.hydrated,
      failed: sticky.failed,
      hadStored: sticky.hadStored,
      appliedFor: personaDefaultsAppliedFor.current,
    });
    if (decision.mark) personaDefaultsAppliedFor.current = persona.active;
    if (!decision.apply) return;
    if (persona.active === 'bl' && blSegments.length > 0) {
      setSegmentFilter(blSegments[0] ?? null);
    } else if (persona.active === 'mtb') {
      setBereichFilter('MTB');
      // Atlas-Segmentfilter ist einwertig → erstes Segment (wie beim BL).
      if (mtbSegments.length > 0) setSegmentFilter(mtbSegments[0] ?? null);
    } else if (persona.active === 'service') {
      setBereichFilter('Service');
      if (svcSegments.length > 0) setSegmentFilter(svcSegments[0] ?? null);
    } else if (persona.active === 'pl') {
      setMyProjectsOnly(true); // PL-Default: nur eigene Projekte (abschaltbar)
    }
    // kl/manager → kein automatischer Atlas-Filter (Atlas ist KL-Default-Sicht)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona.active, persona.isLoading, sticky.hydrated, sticky.hadStored, sticky.failed]);

  const items = projectsQuery.data?.items ?? [];
  const phases = (phasesQuery.data?.items ?? []).slice().sort((a, b) => a.order - b.order);

  const todayIso = new Date().toISOString().slice(0, 10);
  const filtered = useMemo(() => {
    return items.filter((p) => {
      // #123: Zusammengeführte Projekte nie — die API liefert sie ohnehin nicht (doppelte Absicherung).
      if (p.lifecycle_status === 'merged') return false;
      if (!includeCompleted && p.lifecycle_status === 'completed') return false;
      if (segmentFilter && p.segment !== segmentFilter) return false;
      if (bereichFilter && p.responsibility !== bereichFilter) return false;
      if (msFilter && p.ms_classification !== msFilter) return false;
      if (needsOnly && !p.staffing_needed) return false;
      if (missingResponsibility && p.project_lead_user_id) return false;
      if (myProjectsOnly && p.project_lead_user_id !== user?.user_id) return false;
      if (overdueOnly) {
        if (!p.due_date || p.lifecycle_status !== 'active' || p.due_date >= todayIso) return false;
      }
      if (dueFrom && (!p.due_date || p.due_date < dueFrom)) return false;
      if (dueTo && (!p.due_date || p.due_date > dueTo)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const hay = `${p.project_key} ${p.customer_name} ${p.name} ${p.segment}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [
    items,
    segmentFilter,
    bereichFilter,
    msFilter,
    needsOnly,
    missingResponsibility,
    myProjectsOnly,
    user?.user_id,
    includeCompleted,
    overdueOnly,
    dueFrom,
    dueTo,
    todayIso,
    search,
  ]);

  // Gruppieren nach (segment, phase_id)
  const cellMap = useMemo(() => {
    const map = new Map<string, Project[]>();
    for (const p of filtered) {
      const key = `${p.segment}::${p.phase_id}`;
      const arr = map.get(key) ?? [];
      arr.push(p);
      map.set(key, arr);
    }
    return map;
  }, [filtered]);

  const totalActive = items.filter((p) => p.lifecycle_status === 'active').length;

  return (
    <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-6">
      {/* Header */}
      <header className="pt-6 animate-fade-up">
        <div
          className="text-[10.5px] uppercase tracking-[0.14em] mb-3"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          {t('v2.atlas.eyebrow', 'Portfolio · Atlas')}
        </div>
        <h1
          className="font-medium leading-[1.05] tracking-tight mb-3"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(22px, 2.4vw, 36px)',
          }}
        >
          {t('v2.atlas.headline_lead', 'Jedes aktive Projekt,')}{' '}
          <em
            className="not-italic"
            style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {t('v2.atlas.headline_emphasis', 'auf einen Blick')}
          </em>
          {t('v2.atlas.headline_trail', '.')}
        </h1>
        <p className="text-sm max-w-[640px]" style={{ color: 'var(--ink-3)' }}>
          {t(
            'v2.atlas.subtitle',
            'Jede Zelle ist ein Projekt, positioniert nach verantwortlichem Segment und aktueller Phase. Hovern zum Inspizieren, klicken zum Eintauchen. Probleme sind farblich markiert.',
          )}
        </p>
      </header>

      {/* Filter-Rail */}
      <FilterRail
        search={search}
        onSearch={setSearch}
        segmentFilter={segmentFilter}
        onSegment={setSegmentFilter}
        bereichFilter={bereichFilter}
        onBereich={setBereichFilter}
        msFilter={msFilter}
        onMs={setMsFilter}
        needsOnly={needsOnly}
        onNeedsOnly={setNeedsOnly}
        missingResponsibility={missingResponsibility}
        onMissingResponsibility={setMissingResponsibility}
        myProjectsOnly={myProjectsOnly}
        onMyProjectsOnly={setMyProjectsOnly}
        includeCompleted={includeCompleted}
        onIncludeCompleted={setIncludeCompleted}
        overdueOnly={overdueOnly}
        onOverdueOnly={setOverdueOnly}
        dueFrom={dueFrom}
        onDueFrom={setDueFrom}
        dueTo={dueTo}
        onDueTo={setDueTo}
      />

      {/* Grid */}
      <AtlasGrid phases={phases} cellMap={cellMap} />

      {/* Footer-Legende */}
      <footer
        className="flex items-center gap-4 pt-4 text-xs"
        style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
      >
        <Legend dot="var(--danger)">{t('v2.atlas.legend_blocked', 'blockiert')}</Legend>
        <Legend dot="var(--warn)">
          {t('v2.atlas.legend_staffing', 'offener Staffing-Bedarf')}
        </Legend>
        <Legend dot="var(--primary)">{t('v2.atlas.legend_qsbl', 'QS mit BL offen')}</Legend>
        <span className="ml-auto">
          {t('v2.atlas.showing', '{{visible}} / {{total}} angezeigt', {
            visible: filtered.length,
            total: totalActive,
          })}
        </span>
      </footer>
    </div>
  );
}

// ===========================================================================
// Filter-Rail
// ===========================================================================

function FilterRail(props: {
  search: string;
  onSearch: (v: string) => void;
  segmentFilter: string | null;
  onSegment: (v: string | null) => void;
  bereichFilter: string | null;
  onBereich: (v: string | null) => void;
  msFilter: string | null;
  onMs: (v: string | null) => void;
  needsOnly: boolean;
  onNeedsOnly: (v: boolean) => void;
  missingResponsibility: boolean;
  onMissingResponsibility: (v: boolean) => void;
  myProjectsOnly: boolean;
  onMyProjectsOnly: (v: boolean) => void;
  includeCompleted: boolean;
  onIncludeCompleted: (v: boolean) => void;
  overdueOnly: boolean;
  onOverdueOnly: (v: boolean) => void;
  dueFrom: string;
  onDueFrom: (v: string) => void;
  dueTo: string;
  onDueTo: (v: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="rounded-xl border p-4 space-y-3 animate-fade-up-d1"
      style={{ background: 'var(--surface)', borderColor: 'var(--line-soft)' }}
    >
      <SearchInput
        value={props.search}
        onChange={props.onSearch}
        placeholder={t('v2.atlas.search_placeholder', 'Project-Key, Kunde, Name…')}
      />

      <ChipRow label={t('v2.atlas.filter_segment', 'Segment')}>
        <Chip active={props.segmentFilter === null} onClick={() => props.onSegment(null)}>
          {t('v2.atlas.filter_all', 'Alle')}
        </Chip>
        {SEGMENTS.map((s) => (
          <Chip key={s} active={props.segmentFilter === s} onClick={() => props.onSegment(s)}>
            {s}
          </Chip>
        ))}
      </ChipRow>

      <ChipRow label={t('v2.atlas.filter_bereich', 'Bereich')}>
        <Chip active={props.bereichFilter === null} onClick={() => props.onBereich(null)}>
          {t('v2.atlas.filter_all', 'Alle')}
        </Chip>
        {RESPONSIBILITIES.map((r) => (
          <Chip key={r} active={props.bereichFilter === r} onClick={() => props.onBereich(r)}>
            {r}
          </Chip>
        ))}
      </ChipRow>

      <ChipRow label={t('v2.atlas.filter_ms', 'Dienstleistungsart')}>
        <Chip active={props.msFilter === null} onClick={() => props.onMs(null)}>
          {t('v2.atlas.filter_all', 'Alle')}
        </Chip>
        {MS_CLASSES.map((m) => (
          <Chip key={m} active={props.msFilter === m} onClick={() => props.onMs(m)}>
            {m}
          </Chip>
        ))}
      </ChipRow>

      <div className="flex flex-wrap gap-2 pt-1">
        <Toggle active={props.needsOnly} onClick={() => props.onNeedsOnly(!props.needsOnly)}>
          {t('v2.atlas.toggle_needs_only', 'Mit offenem Bedarf')}
        </Toggle>
        <Toggle
          active={props.missingResponsibility}
          onClick={() => props.onMissingResponsibility(!props.missingResponsibility)}
        >
          {t('v2.atlas.toggle_missing_responsibility', 'Fehlende Verantwortung')}
        </Toggle>
        <Toggle
          active={props.myProjectsOnly}
          onClick={() => props.onMyProjectsOnly(!props.myProjectsOnly)}
        >
          {t('v2.atlas.toggle_my_projects', 'Nur meine Projekte')}
        </Toggle>
        <Toggle active={props.overdueOnly} onClick={() => props.onOverdueOnly(!props.overdueOnly)}>
          {t('v2.duedate.filter_overdue_only', 'Nur überfällig')}
        </Toggle>
        <Toggle
          active={props.includeCompleted}
          onClick={() => props.onIncludeCompleted(!props.includeCompleted)}
        >
          {t('v2.atlas.toggle_include_completed', 'Abgeschlossene anzeigen')}
        </Toggle>
      </div>

      <DueRangeFilter
        label={t('v2.duedate.label', 'Fälligkeit')}
        fromLabel={t('v2.duedate.filter_from', 'Fällig ab')}
        toLabel={t('v2.duedate.filter_to', 'Fällig bis')}
        from={props.dueFrom}
        to={props.dueTo}
        onFrom={props.onDueFrom}
        onTo={props.onDueTo}
      />
    </div>
  );
}

// ===========================================================================
// Atlas Grid
// ===========================================================================

function AtlasGrid({
  phases,
  cellMap,
}: {
  phases: Array<{ phase_id: string; name: string; order: number }>;
  cellMap: Map<string, Project[]>;
}) {
  if (phases.length === 0) return null;

  const gridTemplate = `200px repeat(${phases.length}, minmax(180px, 1fr))`;

  return (
    <div className="overflow-x-auto -mx-2 px-2 animate-fade-up-d2">
      <div
        className="grid gap-px"
        style={{
          gridTemplateColumns: gridTemplate,
          background: 'var(--line-soft)',
          border: '1px solid var(--line-soft)',
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        {/* Header-Zeile */}
        <div style={{ background: 'var(--surface)' }} />
        {phases.map((ph) => (
          <div key={ph.phase_id} className="px-3 py-2.5" style={{ background: 'var(--surface)' }}>
            <span
              className="text-[10px] uppercase tracking-[0.14em]"
              style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
            >
              {String(ph.order).padStart(2, '0')} · {ph.name}
            </span>
          </div>
        ))}

        {/* Segment-Zeilen */}
        {SEGMENTS.map((seg) => (
          <SegmentRow key={seg} segment={seg} phases={phases} cellMap={cellMap} />
        ))}
      </div>
    </div>
  );
}

function SegmentRow({
  segment,
  phases,
  cellMap,
}: {
  segment: Project['segment'];
  phases: Array<{ phase_id: string; name: string; order: number }>;
  cellMap: Map<string, Project[]>;
}) {
  const { t } = useTranslation();
  const segmentTotal = phases.reduce(
    (sum, ph) => sum + (cellMap.get(`${segment}::${ph.phase_id}`)?.length ?? 0),
    0,
  );
  return (
    <>
      <div className="px-4 py-3" style={{ background: 'var(--surface)' }}>
        <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {segment}
        </div>
        <div className="text-[10px]" style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}>
          {t('v2.mc.active_projects', '{{count}} aktiv', { count: segmentTotal })}
        </div>
      </div>
      {phases.map((ph) => {
        const projects = cellMap.get(`${segment}::${ph.phase_id}`) ?? [];
        return <AtlasCell key={ph.phase_id} projects={projects} />;
      })}
    </>
  );
}

function AtlasCell({ projects }: { projects: Project[] }) {
  if (projects.length === 0) {
    return (
      <div
        className="min-h-[88px]"
        style={{
          background: 'var(--bg-deeper)',
          backgroundImage:
            'repeating-linear-gradient(45deg, transparent 0, transparent 6px, var(--line-soft) 6px, var(--line-soft) 7px)',
          opacity: 0.4,
        }}
      />
    );
  }
  if (projects.length === 1) {
    const p = projects[0]!;
    return <ProjectPip project={p} />;
  }
  return <MultipleCell projects={projects} />;
}

function ProjectPip({ project: p }: { project: Project }) {
  const { t } = useTranslation();
  const usersQuery = useQuery({ queryKey: ['users-all'], queryFn: () => api.listUsers() });
  const users = usersQuery.data?.items ?? [];
  const flag = p.is_blocked
    ? 'danger'
    : p.staffing_needed
      ? 'warn'
      : p.qs_with_bl_open
        ? 'primary'
        : null;
  const accentBorder =
    flag === 'danger'
      ? 'var(--danger)'
      : flag === 'warn'
        ? 'var(--warn)'
        : flag === 'primary'
          ? 'var(--primary)'
          : 'transparent';
  return (
    <Link
      to={`/projects/${p.project_id}`}
      className="block p-3 transition-all hover:scale-[1.02] hover:z-10 relative group"
      style={{
        background: 'var(--surface)',
        boxShadow: flag ? `inset 0 0 0 1px ${accentBorder}` : 'none',
        textDecoration: 'none',
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-2)' }}>
          {p.project_key}
        </span>
        {flag && (
          <span className="w-1.5 h-1.5 rounded-full ml-auto" style={{ background: accentBorder }} />
        )}
      </div>
      <div className="text-[13px] font-medium leading-tight mb-0.5" style={{ color: 'var(--ink)' }}>
        {p.customer_name}
      </div>
      <div className="text-[11px] leading-tight truncate" style={{ color: 'var(--ink-3)' }}>
        {p.name}
      </div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {!p.project_lead_user_id ? (
          <span
            className="text-[10px] px-1.5 py-0.5 rounded"
            style={{
              background: 'var(--danger-soft)',
              color: 'var(--danger)',
              fontFamily: 'var(--f-mono)',
            }}
          >
            ◇ {t('v2.detail.rail_project_lead', 'Project Lead')}
          </span>
        ) : (
          <span
            className="w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-medium"
            style={{ background: 'var(--surface-3)', color: 'var(--ink-2)' }}
          >
            {avatarInitials(p.project_lead_user_id, users)}
          </span>
        )}
        {p.staffing_needed && (
          <span
            className="text-[10px]"
            style={{ color: 'var(--warn)', fontFamily: 'var(--f-mono)' }}
          >
            +staff
          </span>
        )}
        <span className="ml-auto">
          <DueDateBadge dueDate={p.due_date} lifecycleStatus={p.lifecycle_status} />
        </span>
      </div>
    </Link>
  );
}

function MultipleCell({ projects }: { projects: Project[] }) {
  const { t } = useTranslation();
  const sample = projects.slice(0, 3);
  const overflow = projects.length - sample.length;
  const hasFlag = projects.some((p) => p.is_blocked || p.staffing_needed || p.qs_with_bl_open);
  return (
    <div className="p-3" style={{ background: 'var(--surface)' }}>
      <div className="flex items-center gap-2 mb-2">
        <span
          className="text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 rounded"
          style={{
            fontFamily: 'var(--f-mono)',
            background: 'var(--surface-2)',
            color: 'var(--ink-2)',
          }}
        >
          {t('v2.atlas.more_in_cell', '{{count}} weitere', { count: projects.length })}
        </span>
        {hasFlag && (
          <span
            className="w-1.5 h-1.5 rounded-full ml-auto"
            style={{ background: 'var(--warn)' }}
          />
        )}
      </div>
      <ul className="space-y-1">
        {sample.map((p) => (
          <li key={p.project_id}>
            <Link
              to={`/projects/${p.project_id}`}
              className="block text-[11px] truncate hover:underline"
              style={{ color: 'var(--ink-2)', textDecoration: 'none' }}
            >
              <span style={{ fontFamily: 'var(--f-mono)' }}>{p.project_key}</span> ·{' '}
              {p.customer_name}
            </Link>
          </li>
        ))}
        {overflow > 0 && (
          <li
            className="text-[10px]"
            style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
          >
            +{overflow}
          </li>
        )}
      </ul>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function Legend({ dot, children }: { dot: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: dot }} />
      {children}
    </span>
  );
}
